import { IconChevronLeft } from '@tabler/icons-react'
import { useState } from 'react'

import {
  createSurveyAnswer,
  SurveyPayload,
} from 'features/onboarding/api/create-survey-answer'
import Button from 'components/ui/button'
import RadioGroup from 'components/ui/radio-group'
import Text from 'components/ui/text'
import {
  ONBOARDING_STEPS,
  TRANSLATION_KEYS,
} from 'features/onboarding/config/steps'
import { ONBOARDING_COMPANY_SIZE_OPTIONS } from 'features/onboarding/constants/industry-options'
import { useOnboarding } from 'features/onboarding/hooks/use-onboarding'
import { translate } from 'utils/translation'
import { LoadingSpinner } from 'components/ui/loading/spinner'
import { sendMessage } from 'utils/functions'

interface Step6Props {
  onNext: () => void
  onPrevious: () => void
}

export const Step6 = ({ onNext, onPrevious }: Step6Props) => {
  const onboardingState = useOnboarding()
  const [isLoading, setIsLoading] = useState(false)

  const handleSelectChange = async (value: string) => {
    setIsLoading(true)
    onboardingState.updateFormData({ step6: { companySize: value } })

    try {
      const surveyData: SurveyPayload = {
        survey_name: 'onboarding_survey',
        survey: [
          {
            question: 'user_role',
            answer:
              `${onboardingState.state.formData.step2?.role}` ||
              'not_specified',
            supplementary_answer:
              onboardingState.state.formData.step2?.jobTitle || 'not_specified',
          },
          {
            question: 'seo_experience_level',
            answer:
              onboardingState.state.formData.step3?.comfortLevel ||
              'not_specified',
          },
          {
            question: 'reason_for_visiting',
            answer:
              onboardingState.state.formData.step4?.motivation ||
              'not_specified',
            supplementary_answer:
              onboardingState.state.formData.step4?.motivation === 'other'
                ? onboardingState.state.formData.step4?.specifiedMotivation ||
                  'not_specified'
                : '',
          },
          {
            question: 'industry',
            answer:
              onboardingState.state.formData.step5?.industry || 'not_specified',
            supplementary_answer:
              onboardingState.state.formData.step5?.industry === 'other'
                ? onboardingState.state.formData.step5?.specifiedIndustry ||
                  'not_specified'
                : '',
          },
          {
            question: 'company_size',
            answer: value,
          },
        ],
      }

      await createSurveyAnswer(surveyData)

      sendMessage('UPDATE_ALERTS', {
        type: 'onboarding_survey_alert',
      })

      onboardingState.completeOnboarding()
      onNext()
    } catch (error) {
      console.error('Failed to submit survey:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="tw-mx-auto tw-flex tw-min-h-[calc(100vh-200px)] tw-w-full tw-max-w-[640px] tw-flex-col">
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-start tw-gap-8 tw-pt-8 tw-text-center">
        <Text
          as="h2"
          id="step-heading"
          className="tw-m-0 tw-text-2xl tw-font-bold md:tw-text-3xl"
          boldClassName="tw-text-orange-500"
        >
          {ONBOARDING_STEPS.step6.title}
        </Text>

        <form className="tw-flex tw-w-full tw-max-w-[320px] tw-flex-col tw-items-start tw-justify-center tw-gap-2">
          <RadioGroup
            name="company-size"
            options={ONBOARDING_COMPANY_SIZE_OPTIONS}
            variant="card"
            className="tw-w-full tw-max-w-[320px]"
            onChange={handleSelectChange}
            disabled={isLoading}
          />
        </form>

        {isLoading && <LoadingSpinner className="tw-stroke-orange-500" />}

        <div className="tw-mt-auto tw-flex tw-justify-center tw-gap-3">
          <Button
            size="lg"
            variant="neutral-outlined"
            onClick={onPrevious}
            disabled={isLoading}
            aria-label={translate(TRANSLATION_KEYS.backButton)}
          >
            <IconChevronLeft size={20} aria-hidden="true" />{' '}
            {translate(TRANSLATION_KEYS.backButton)}
          </Button>
        </div>
      </div>
    </section>
  )
}
